'use client';

import { useState } from 'react';
import { Dialog } from '@pixum/web-dialog';
import { DIALOG_STYLE } from '../../../../constants';
import { useImageStateStore } from 'stores/express-designer';
import type { UploadErrorBaseProps } from '../../../../types';
import { UploadErrorDialogContent } from '../UploadErrorDialogContent';
import styles from '../upload-error-dialog.module.css';
import { UploadErrorRemoveRetry } from './UploadErrorRemoveRetry';
import { replacePlaceholderContent } from '../../utils/replacePlaceholderContent';

const createClearedErrorState = (uuid: string) => ({ uuid, uploadErrorType: undefined, errorType: undefined });

export function UploadErrorRetry({
  onClose,
  uploadErrors,
  uploadErrorStateActions,
  imageStateActions,
  uploadService,
  constants
}: UploadErrorBaseProps) {
  const [errorIndex, setErrorIndex] = useState(0);
  const [applyForAll, setApplyForAll] = useState(true);

  const currentError = uploadErrors[errorIndex];
  const thumbnailUrl = useImageStateStore(s => s.images.find(img => img.uuid === currentError?.uuid)?.urlThumbSmall);

  const remainingCount = uploadErrors.length - errorIndex;
  const uuids = uploadErrors.map(e => e.uuid);

  const goToNextErrorOrClose = () => {
    if (errorIndex + 1 >= uploadErrors.length) {
      onClose?.();
    } else {
      setErrorIndex(i => i + 1);
    }
  };

  const handleRemove = () => {
    if (applyForAll) {
      uploadErrorStateActions.removeUploadErrors(uuids);
      imageStateActions.removeImages(uuids);
      onClose?.();
    } else {
      uploadErrorStateActions.removeUploadError(currentError.uuid);
      imageStateActions.removeImages([currentError.uuid]);
      goToNextErrorOrClose();
    }
  };

  const handleRetry = () => {
    if (applyForAll) {
      imageStateActions.updateImages(uploadErrors.map(e => createClearedErrorState(e.uuid)));
      uploadErrorStateActions.removeUploadErrors(uuids);
      uploadErrors.forEach(e => uploadService.retryUpload(e.upload?.uuid ?? e.uuid, e.file));
      onClose?.();
    } else {
      imageStateActions.updateImages([createClearedErrorState(currentError.uuid)]);
      uploadErrorStateActions.removeUploadError(currentError.uuid);
      uploadService.retryUpload(currentError.upload?.uuid ?? currentError.uuid, currentError.file);
      goToNextErrorOrClose();
    }
  };

  const title = constants['i18n_upload_error_retry_title'] ?? '';
  const contentText = replacePlaceholderContent(constants['i18n_upload_error_retry_content'], {
    '%filename%': currentError?.file.name ?? ''
  });

  return (
    <Dialog
      open
      className={styles.dialog}
      onClose={handleRemove}
      showCloseButton={false}
      style={DIALOG_STYLE}
      content={
        <UploadErrorDialogContent
          imageUrl={thumbnailUrl}
          title={title}
          contentText={contentText}
          files={[currentError?.file].filter(Boolean) as File[]}
        />
      }
      footer={
        <UploadErrorRemoveRetry
          errorCount={remainingCount}
          applyForAll={applyForAll}
          onApplyForAllChange={setApplyForAll}
          onRemove={handleRemove}
          onRetry={handleRetry}
          constants={constants}
        />
      }
    />
  );
}
